// Latest version - v2

import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/* ─────────────────────────────────────────────
   DESIGN TOKENS
   Apple-inspired: SF Pro system stack, refined
   neutrals, spacious layouts, subtle depth.
   ───────────────────────────────────────────── */

const T = {
  // Colors
  bg: '#FAFAFA',
  surface: '#FFFFFF',
  surfaceHover: '#F5F5F7',
  border: '#E5E5EA',
  borderLight: '#F0F0F2',
  text: '#1D1D1F',
  textSecondary: '#6E6E73',
  textTertiary: '#AEAEB2',
  accent: '#0071E3',
  accentHover: '#0077ED',
  accentLight: '#E8F2FE',
  accentGradient: 'linear-gradient(135deg, #0071E3 0%, #5856D6 100%)',
  heroGradient: 'linear-gradient(180deg, #000000 0%, #1D1D1F 100%)',
  success: '#34C759',
  warning: '#FF9F0A',
  error: '#FF3B30',
  purple: '#5856D6',
  purpleLight: '#F3F2FF',

  // Typography — system SF Pro stack
  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif',
  fontMono: '"SF Mono", SFMono-Regular, ui-monospace, Menlo, monospace',

  // Spacing
  radius: '12px',
  radiusLg: '16px',
  radiusXl: '20px',
  radiusFull: '980px',

  // Shadows
  shadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)',
  shadowMd: '0 4px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
  shadowLg: '0 10px 40px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
  shadowXl: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',

  // Transitions
  ease: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
};

/* ─────────────────────────────────────────────
   GLOBAL STYLES injected once
   ───────────────────────────────────────────── */

const globalStyleId = 'repurpose-global';
if (typeof document !== 'undefined' && !document.getElementById(globalStyleId)) {
  const style = document.createElement('style');
  style.id = globalStyleId;
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
    body { font-family: ${T.fontFamily}; color: ${T.text}; background: ${T.bg}; line-height: 1.5; }
    input, textarea, select, button { font-family: inherit; }
    button { cursor: pointer; border: none; background: none; }
    a { text-decoration: none; color: inherit; }

    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.6; }
    }
    @keyframes scaleIn {
      from { opacity: 0; transform: scale(0.95); }
      to   { opacity: 1; transform: scale(1); }
    }

    .fade-in-up { animation: fadeInUp 0.6s ${T.ease} both; }
    .fade-in-up-1 { animation-delay: 0.05s; }
    .fade-in-up-2 { animation-delay: 0.1s; }
    .fade-in-up-3 { animation-delay: 0.15s; }
    .fade-in-up-4 { animation-delay: 0.2s; }
    .fade-in-up-5 { animation-delay: 0.25s; }

    ::selection { background: ${T.accentLight}; color: ${T.accent}; }

    input:focus, textarea:focus, select:focus {
      outline: none;
      border-color: ${T.accent} !important;
      box-shadow: 0 0 0 3px ${T.accentLight} !important;
    }

    /* Custom scrollbar */
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: ${T.borderLight}; border-radius: 3px; }
    ::-webkit-scrollbar-thumb:hover { background: ${T.border}; }
  `;
  document.head.appendChild(style);
}

/* ─────────────────────────────────────────────
   REUSABLE PRIMITIVES
   ───────────────────────────────────────────── */

function Icon({ name, size = 20, color = 'currentColor', style: extraStyle }) {
  const icons = {
    repurpose: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={extraStyle}>
        <path d="M12 2L4 7V17L12 22L20 17V7L12 2Z" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M12 8V16" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M8 10L12 8L16 10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M8 14L12 16L16 14" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    arrowRight: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={extraStyle}>
        <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    upload: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={extraStyle}>
        <path d="M12 16V4M12 4L8 8M12 4L16 8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M20 16V18C20 19.1 19.1 20 18 20H6C4.9 20 4 19.1 4 18V16" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    sparkle: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={extraStyle}>
        <path d="M12 2L14.4 9.6L22 12L14.4 14.4L12 22L9.6 14.4L2 12L9.6 9.6L12 2Z" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
      </svg>
    ),
    credit: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={extraStyle}>
        <rect x="2" y="5" width="20" height="14" rx="2" stroke={color} strokeWidth="1.5"/>
        <path d="M2 10H22" stroke={color} strokeWidth="1.5"/>
      </svg>
    ),
    logout: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={extraStyle}>
        <path d="M9 21H5C3.89 21 3 20.1 3 19V5C3 3.9 3.89 3 5 3H9" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M16 17L21 12L16 7" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M21 12H9" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    check: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={extraStyle}>
        <path d="M20 6L9 17L4 12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    copy: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={extraStyle}>
        <rect x="9" y="9" width="13" height="13" rx="2" stroke={color} strokeWidth="1.5"/>
        <path d="M5 15H4C2.9 15 2 14.1 2 13V4C2 2.9 2.9 2 4 2H13C14.1 2 15 2.9 15 4V5" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    tiktok: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={extraStyle}>
        <path d="M9 12C6.8 12 5 13.8 5 16S6.8 20 9 20 13 18.2 13 16V4C13.5 6.5 16 8 19 8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    linkedin: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={extraStyle}>
        <rect x="3" y="3" width="18" height="18" rx="2" stroke={color} strokeWidth="1.5"/>
        <path d="M8 11V16M8 8V8.01M12 16V11M16 16V13C16 11.9 15.1 11 14 11S12 11.9 12 13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    twitter: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={extraStyle}>
        <path d="M4 4L10.5 12.5M20 20L13.5 11.5M10.5 12.5L4 20H7L13.5 11.5M10.5 12.5L17 4H20L13.5 11.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    instagram: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={extraStyle}>
        <rect x="2" y="2" width="20" height="20" rx="5" stroke={color} strokeWidth="1.5"/>
        <circle cx="12" cy="12" r="5" stroke={color} strokeWidth="1.5"/>
        <circle cx="17.5" cy="6.5" r="1" fill={color}/>
      </svg>
    ),
    mail: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={extraStyle}>
        <rect x="2" y="4" width="20" height="16" rx="2" stroke={color} strokeWidth="1.5"/>
        <path d="M2 7L12 13L22 7" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    fileText: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={extraStyle}>
        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M14 2V8H20" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M8 13H16M8 17H13" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    mic: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={extraStyle}>
        <rect x="9" y="2" width="6" height="11" rx="3" stroke={color} strokeWidth="1.5"/>
        <path d="M5 10C5 13.9 8.1 17 12 17S19 13.9 19 10" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M12 17V21M9 21H15" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    video: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={extraStyle}>
        <rect x="2" y="4" width="15" height="16" rx="2" stroke={color} strokeWidth="1.5"/>
        <path d="M17 8L22 5V19L17 16" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
      </svg>
    ),
    trash: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={extraStyle}>
        <path d="M3 6H21" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M8 6V4C8 3.45 8.45 3 9 3H15C15.55 3 16 3.45 16 4V6" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M5 6L6 20C6 20.55 6.45 21 7 21H17C17.55 21 18 20.55 18 20L19 6" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M10 10V17" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M14 10V17" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  };
  return icons[name] || null;
}

function Button({ children, variant = 'primary', size = 'md', icon, loading, disabled, onClick, style: extraStyle, ...props }) {
  const [hover, setHover] = useState(false);

  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontWeight: 500,
    borderRadius: T.radiusFull,
    transition: `all 0.25s ${T.ease}`,
    letterSpacing: '-0.01em',
    border: 'none',
    cursor: disabled || loading ? 'default' : 'pointer',
    opacity: disabled ? 0.4 : 1,
    whiteSpace: 'nowrap',
  };

  const sizes = {
    sm: { padding: '8px 16px', fontSize: '13px' },
    md: { padding: '10px 22px', fontSize: '14px' },
    lg: { padding: '14px 32px', fontSize: '16px' },
  };

  const variants = {
    primary: {
      background: T.accent,
      color: '#FFF',
      ...(hover && !disabled ? { background: T.accentHover, transform: 'translateY(-1px)', boxShadow: `0 4px 16px rgba(0,113,227,0.3)` } : {}),
    },
    secondary: {
      background: T.surfaceHover,
      color: T.text,
      ...(hover && !disabled ? { background: T.border } : {}),
    },
    ghost: {
      background: 'transparent',
      color: T.textSecondary,
      ...(hover && !disabled ? { color: T.text, background: T.surfaceHover } : {}),
    },
    gradient: {
      background: T.accentGradient,
      color: '#FFF',
      ...(hover && !disabled ? { transform: 'translateY(-1px)', boxShadow: `0 6px 24px rgba(0,113,227,0.35)` } : {}),
    },
    outline: {
      background: 'transparent',
      color: T.accent,
      border: `1.5px solid ${T.accent}`,
      ...(hover && !disabled ? { background: T.accentLight } : {}),
    },
  };

  return (
    <button
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={disabled || loading ? undefined : onClick}
      style={{ ...base, ...sizes[size], ...variants[variant], ...extraStyle }}
      {...props}
    >
      {loading ? <span style={{ animation: 'pulse 1.2s infinite' }}>Processing…</span> : (
        <>
          {icon && <Icon name={icon} size={size === 'lg' ? 18 : 16} />}
          {children}
        </>
      )}
    </button>
  );
}

function Input({ label, icon, style: extraStyle, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {label && (
        <label style={{ fontSize: '13px', fontWeight: 500, color: T.textSecondary, letterSpacing: '-0.01em' }}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        {icon && (
          <div style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: T.textTertiary }}>
            <Icon name={icon} size={18} />
          </div>
        )}
        <input
          style={{
            width: '100%',
            padding: icon ? '12px 16px 12px 44px' : '12px 16px',
            fontSize: '15px',
            border: `1px solid ${T.border}`,
            borderRadius: T.radius,
            background: T.surface,
            color: T.text,
            transition: `all 0.2s ${T.ease}`,
            ...extraStyle,
          }}
          {...props}
        />
      </div>
    </div>
  );
}

function TextArea({ label, style: extraStyle, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {label && (
        <label style={{ fontSize: '13px', fontWeight: 500, color: T.textSecondary, letterSpacing: '-0.01em' }}>
          {label}
        </label>
      )}
      <textarea
        style={{
          width: '100%',
          padding: '12px 16px',
          fontSize: '15px',
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          background: T.surface,
          color: T.text,
          resize: 'vertical',
          minHeight: '120px',
          lineHeight: 1.6,
          transition: `all 0.2s ${T.ease}`,
          ...extraStyle,
        }}
        {...props}
      />
    </div>
  );
}

function Card({ children, style: extraStyle, hover = false, ...props }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => hover && setHovered(true)}
      onMouseLeave={() => hover && setHovered(false)}
      style={{
        background: T.surface,
        borderRadius: T.radiusLg,
        border: `1px solid ${T.border}`,
        boxShadow: hovered ? T.shadowMd : T.shadow,
        transition: `all 0.3s ${T.ease}`,
        transform: hovered ? 'translateY(-2px)' : 'none',
        ...extraStyle,
      }}
      {...props}
    >
      {children}
    </div>
  );
}

function Badge({ children, variant = 'default', style: extraStyle }) {
  const styles = {
    default: { background: T.surfaceHover, color: T.textSecondary },
    accent: { background: T.accentLight, color: T.accent },
    purple: { background: T.purpleLight, color: T.purple },
    success: { background: '#E8FAE8', color: T.success },
  };
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 10px',
      fontSize: '12px',
      fontWeight: 600,
      borderRadius: T.radiusFull,
      letterSpacing: '0.02em',
      textTransform: 'uppercase',
      ...styles[variant],
      ...extraStyle,
    }}>
      {children}
    </span>
  );
}

/* ─────────────────────────────────────────────
   AUTH CONTEXT
   ───────────────────────────────────────────── */

const AuthContext = React.createContext();

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      if (token) {
        try {
          const res = await fetch(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            setUser(data);
          } else {
            localStorage.removeItem('token');
            setToken(null);
          }
        } catch (err) {
          console.error('Auth error:', err);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    init();
  }, [token]);

  const login = (newToken, userData) => {
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('token', newToken);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  return React.useContext(AuthContext);
}

/* ─────────────────────────────────────────────
   AUTH SCREENS
   ───────────────────────────────────────────── */

function AuthScreen({ onSwitch }) {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();

  const handleSwitch = (m) => { setMode(m); setError(''); };

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';

    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong.');
        return;
      }
      login(data.token, data.user);
    } catch (err) {
      setError('Unable to connect. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: T.heroGradient,
      color: '#FFF',
    }}>
      {/* Minimal nav */}
      <nav style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '20px 40px',
        maxWidth: '1200px',
        width: '100%',
        margin: '0 auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icon name="repurpose" size={28} color="#FFF" />
          <span style={{ fontSize: '20px', fontWeight: 600, letterSpacing: '-0.03em' }}>Repurpose AI</span>
        </div>
      </nav>

      {/* Center content */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 24px',
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '48px',
          maxWidth: '440px',
          width: '100%',
        }}>
          {/* Hero copy */}
          <div className="fade-in-up" style={{ textAlign: 'center' }}>
            <h1 style={{
              fontSize: '40px',
              fontWeight: 700,
              letterSpacing: '-0.04em',
              lineHeight: 1.1,
              marginBottom: '12px',
            }}>
              One piece of content.
              <br />
              <span style={{ background: 'linear-gradient(90deg, #5AC8FA, #5856D6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Infinite reach.
              </span>
            </h1>
            <p style={{ fontSize: '17px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>
              AI-powered content repurposing for creators and agencies.
            </p>
          </div>

          {/* Auth card */}
          <div className="fade-in-up fade-in-up-2" style={{
            background: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: T.radiusXl,
            padding: '36px',
            width: '100%',
          }}>
            {/* Tabs */}
            <div style={{
              display: 'flex',
              background: 'rgba(255,255,255,0.08)',
              borderRadius: T.radiusFull,
              padding: '3px',
              marginBottom: '28px',
            }}>
              {['login', 'register'].map(m => (
                <button
                  key={m}
                  onClick={() => handleSwitch(m)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    fontSize: '14px',
                    fontWeight: 500,
                    borderRadius: T.radiusFull,
                    color: mode === m ? '#FFF' : 'rgba(255,255,255,0.5)',
                    background: mode === m ? 'rgba(255,255,255,0.14)' : 'transparent',
                    transition: `all 0.25s ${T.ease}`,
                  }}
                >
                  {m === 'login' ? 'Sign In' : 'Create Account'}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={{
                  padding: '14px 16px',
                  fontSize: '15px',
                  borderRadius: T.radius,
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#FFF',
                  outline: 'none',
                  transition: `all 0.2s ${T.ease}`,
                }}
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                style={{
                  padding: '14px 16px',
                  fontSize: '15px',
                  borderRadius: T.radius,
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#FFF',
                  outline: 'none',
                  transition: `all 0.2s ${T.ease}`,
                }}
              />

              {error && (
                <p style={{
                  fontSize: '13px',
                  color: '#FF6961',
                  padding: '8px 12px',
                  background: 'rgba(255,59,48,0.1)',
                  borderRadius: '8px',
                }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                style={{
                  padding: '14px',
                  fontSize: '15px',
                  fontWeight: 600,
                  borderRadius: T.radiusFull,
                  background: T.accent,
                  color: '#FFF',
                  border: 'none',
                  cursor: submitting ? 'default' : 'pointer',
                  opacity: submitting ? 0.7 : 1,
                  transition: `all 0.25s ${T.ease}`,
                  marginTop: '4px',
                }}
              >
                {submitting ? 'Processing…' : mode === 'login' ? 'Sign In' : 'Get Started — Free'}
              </button>
            </form>

            {mode === 'register' && (
              <p style={{
                textAlign: 'center',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.4)',
                marginTop: '16px',
              }}>
                Free plan includes 3 repurposings per month. No credit card needed.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   NAVIGATION
   ───────────────────────────────────────────── */

function Nav({ user, onLogout }) {
  return (
    <nav style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 32px',
      background: 'rgba(255,255,255,0.8)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderBottom: `1px solid ${T.borderLight}`,
      position: 'sticky',
      top: 0,
      zIndex: 100,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Icon name="repurpose" size={26} color={T.text} />
        <span style={{ fontSize: '18px', fontWeight: 600, letterSpacing: '-0.03em', color: T.text }}>
          Repurpose AI
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Badge variant={user?.plan === 'free' ? 'default' : 'accent'}>
          {user?.plan === 'free' ? 'Free' : user?.plan} plan
        </Badge>

        <button
          onClick={onLogout}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: T.textSecondary,
            fontSize: '14px',
            fontWeight: 500,
            padding: '8px 12px',
            borderRadius: '8px',
            transition: `all 0.2s ${T.ease}`,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = T.surfaceHover; e.currentTarget.style.color = T.text; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.textSecondary; }}
        >
          <Icon name="logout" size={16} />
          Sign Out
        </button>
      </div>
    </nav>
  );
}

/* ─────────────────────────────────────────────
   TAB BAR
   ───────────────────────────────────────────── */

function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{
      display: 'flex',
      gap: '4px',
      padding: '4px',
      background: T.surfaceHover,
      borderRadius: T.radiusFull,
      width: 'fit-content',
    }}>
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: active === tab.id ? 600 : 400,
            color: active === tab.id ? T.text : T.textSecondary,
            background: active === tab.id ? T.surface : 'transparent',
            borderRadius: T.radiusFull,
            boxShadow: active === tab.id ? T.shadow : 'none',
            transition: `all 0.25s ${T.ease}`,
          }}
        >
          <Icon name={tab.icon} size={16} />
          {tab.label}
          {tab.count !== undefined && (
            <span style={{
              background: active === tab.id ? T.accentLight : T.border,
              color: active === tab.id ? T.accent : T.textSecondary,
              fontSize: '11px',
              fontWeight: 600,
              padding: '2px 7px',
              borderRadius: '20px',
            }}>
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   UPLOAD PAGE
   ───────────────────────────────────────────── */

const OUTPUT_TYPES = [
  { id: 'tiktok_clip', label: 'TikTok Hook', icon: 'tiktok', color: '#FE2C55' },
  { id: 'linkedin_post', label: 'LinkedIn Post', icon: 'linkedin', color: '#0A66C2' },
  { id: 'twitter_thread', label: 'X Thread', icon: 'twitter', color: '#1D1D1F' },
  { id: 'reddit_post', label: 'Reddit Post', icon: 'fileText', color: '#FF4500' },
  { id: 'instagram_caption', label: 'Instagram Caption', icon: 'instagram', color: '#E1306C' },
  { id: 'email_summary', label: 'Email Summary', icon: 'mail', color: '#FF9F0A' },
  { id: 'blog_outline', label: 'Blog Outline', icon: 'fileText', color: '#34C759' },
  { id: 'podcast_description', label: 'Podcast Description', icon: 'mic', color: '#5856D6' },
];

function UploadPage({ content, onRefresh, token }) {
  const [title, setTitle] = useState('');
  const [contentType, setContentType] = useState('video');
  const [sourceUrl, setSourceUrl] = useState('');
  const [transcript, setTranscript] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [outputType, setOutputType] = useState('tiktok_clip');
  const [repurposing, setRepurposing] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [uploadMode, setUploadMode] = useState('text');
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  async function handleUpload(e) {
    e.preventDefault();
    setUploading(true);
    try {
      const res = await fetch(`${API_URL}/api/content/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title, contentType, sourceUrl, transcript }),
      });
      if (res.ok) {
        setTitle(''); setSourceUrl(''); setTranscript('');
        onRefresh();
      }
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  }

  async function handleFileUpload(e) {
    e.preventDefault();
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (title) formData.append('title', title);

      const res = await fetch(`${API_URL}/api/content/upload-file`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (res.ok) {
        setTitle(''); setSelectedFile(null);
        onRefresh();
      } else {
        const err = await res.json();
        setMessage(err.error || 'Failed to process file.'); setIsError(true);
      }
    } catch (err) {
      console.error('File upload error:', err);
    } finally {
      setUploading(false);
    }
  }

  function handleFileDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) setSelectedFile(file);
  }

  function handleFileSelect(e) {
    const file = e.target.files[0];
    if (file) setSelectedFile(file);
  }

  async function handleRepurpose() {
    if (!selected) return;
    setRepurposing(true);
    setMessage(''); setIsError(false);
    try {
      const res = await fetch(`${API_URL}/api/repurpose`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contentId: selected.id, outputType }),
      });
      if (res.ok) {
        setMessage('Content generated. Check the Generated tab.'); setIsError(false);
        setSelected(null);
        onRefresh();
      } else {
        const err = await res.json();
        setMessage(err.error || 'Something went wrong. Please try again.'); setIsError(true);
      }
    } catch (err) {
      console.error('Repurpose error:', err);
    } finally {
      setRepurposing(false);
    }
  }

  async function deleteContent(e, id) {
    e.stopPropagation();
    try {
      const res = await fetch(`${API_URL}/api/content/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        if (selected?.id === id) setSelected(null);
        onRefresh();
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  }

  const contentTypes = [
    { id: 'video', label: 'Video', icon: 'video' },
    { id: 'audio', label: 'Audio', icon: 'mic' },
    { id: 'text', label: 'Text', icon: 'fileText' },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' }}>
      {/* Left: Upload */}
      <Card className="fade-in-up" style={{ padding: '32px' }}>
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 600, letterSpacing: '-0.03em', marginBottom: '4px' }}>Add content</h2>
          <p style={{ fontSize: '14px', color: T.textSecondary }}>Upload a file or paste text to Repurpose AI.</p>
        </div>

        {/* Upload mode toggle */}
        <div style={{
          display: 'flex',
          gap: '4px',
          padding: '3px',
          background: T.surfaceHover,
          borderRadius: T.radiusFull,
          marginBottom: '20px',
        }}>
          {[{ id: 'text', label: 'Paste Text' }, { id: 'file', label: 'Upload File' }].map(m => (
            <button
              key={m.id}
              type="button"
              onClick={() => setUploadMode(m.id)}
              style={{
                flex: 1,
                padding: '9px',
                fontSize: '13px',
                fontWeight: 500,
                borderRadius: T.radiusFull,
                color: uploadMode === m.id ? T.text : T.textSecondary,
                background: uploadMode === m.id ? T.surface : 'transparent',
                boxShadow: uploadMode === m.id ? T.shadow : 'none',
                transition: `all 0.25s ${T.ease}`,
              }}
            >
              {m.label}
            </button>
          ))}
        </div>

        {uploadMode === 'text' ? (
          <form onSubmit={handleUpload} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Input label="Title" placeholder="e.g. My latest podcast episode" value={title} onChange={e => setTitle(e.target.value)} required />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: 500, color: T.textSecondary }}>Type</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {contentTypes.map(ct => (
                  <button
                    key={ct.id}
                    type="button"
                    onClick={() => setContentType(ct.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 16px',
                      fontSize: '14px',
                      fontWeight: 500,
                      borderRadius: T.radiusFull,
                      border: `1.5px solid ${contentType === ct.id ? T.accent : T.border}`,
                      background: contentType === ct.id ? T.accentLight : T.surface,
                      color: contentType === ct.id ? T.accent : T.textSecondary,
                      transition: `all 0.2s ${T.ease}`,
                    }}
                  >
                    <Icon name={ct.icon} size={16} />
                    {ct.label}
                  </button>
                ))}
              </div>
            </div>

            <Input label="Source URL (optional)" placeholder="https://youtube.com/watch?v=…" type="url" value={sourceUrl} onChange={e => setSourceUrl(e.target.value)} />

            <TextArea label="Transcript or full text" placeholder="Paste the content you want to repurpose…" value={transcript} onChange={e => setTranscript(e.target.value)} required />

            <Button variant="primary" size="lg" loading={uploading} icon="upload" style={{ width: '100%' }} type="submit">
              Upload Content
            </Button>
          </form>
        ) : (
          <form onSubmit={handleFileUpload} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Input label="Title (optional)" placeholder="Auto-detected from filename" value={title} onChange={e => setTitle(e.target.value)} />

            {/* Drop zone */}
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleFileDrop}
              onClick={() => document.getElementById('file-input').click()}
              style={{
                border: `2px dashed ${dragOver ? T.accent : selectedFile ? T.success : T.border}`,
                borderRadius: T.radiusLg,
                padding: '40px 24px',
                textAlign: 'center',
                cursor: 'pointer',
                background: dragOver ? T.accentLight : selectedFile ? 'rgba(52,199,89,0.04)' : T.surface,
                transition: `all 0.2s ${T.ease}`,
              }}
            >
              <input
                id="file-input"
                type="file"
                accept=".pdf,.docx,.doc,.txt,.md,.csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />

              {selectedFile ? (
                <div>
                  <Icon name="check" size={28} color={T.success} style={{ margin: '0 auto 8px', display: 'block' }} />
                  <p style={{ fontSize: '14px', fontWeight: 500, color: T.text, marginBottom: '4px' }}>
                    {selectedFile.name}
                  </p>
                  <p style={{ fontSize: '12px', color: T.textTertiary }}>
                    {(selectedFile.size / 1024).toFixed(0)} KB — Click to change
                  </p>
                </div>
              ) : (
                <div>
                  <Icon name="upload" size={28} color={dragOver ? T.accent : T.textTertiary} style={{ margin: '0 auto 8px', display: 'block' }} />
                  <p style={{ fontSize: '14px', fontWeight: 500, color: T.textSecondary, marginBottom: '4px' }}>
                    Drop a file here or click to browse
                  </p>
                  <p style={{ fontSize: '12px', color: T.textTertiary }}>
                    PDF, Word (.docx), or text files up to 20MB
                  </p>
                </div>
              )}
            </div>

            <Button variant="primary" size="lg" loading={uploading} icon="upload" style={{ width: '100%' }} type="submit" disabled={!selectedFile}>
              {uploading ? 'Processing…' : 'Upload File'}
            </Button>
          </form>
        )}
      </Card>

      {/* Right: Content list + repurpose */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <Card className="fade-in-up fade-in-up-1" style={{ padding: '32px' }}>
          <div style={{ marginBottom: '20px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 600, letterSpacing: '-0.03em', marginBottom: '4px' }}>Your content</h2>
            <p style={{ fontSize: '14px', color: T.textSecondary }}>Select a piece to repurpose.</p>
          </div>

          {content.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: T.textTertiary }}>
              <Icon name="upload" size={32} color={T.textTertiary} style={{ margin: '0 auto 12px', display: 'block' }} />
              <p style={{ fontSize: '14px' }}>No content yet. Upload something to get started.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {content.map(item => (
                <div
                  key={item.id}
                  onClick={() => setSelected(selected?.id === item.id ? null : item)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '14px 16px',
                    textAlign: 'left',
                    borderRadius: T.radius,
                    border: `1.5px solid ${selected?.id === item.id ? T.accent : T.border}`,
                    background: selected?.id === item.id ? T.accentLight : T.surface,
                    transition: `all 0.2s ${T.ease}`,
                    width: '100%',
                    cursor: 'pointer',
                  }}
                >
                  <Icon
                    name={item.content_type === 'audio' ? 'mic' : item.content_type === 'pdf' ? 'fileText' : item.content_type === 'document' ? 'fileText' : item.content_type === 'text' ? 'fileText' : 'video'}
                    size={18}
                    color={selected?.id === item.id ? T.accent : T.textTertiary}
                  />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '14px', fontWeight: 500, color: T.text }}>{item.title}</p>
                    <p style={{ fontSize: '12px', color: T.textTertiary, marginTop: '2px' }}>{item.content_type}</p>
                  </div>
                  <button
                    onClick={(e) => deleteContent(e, item.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '30px',
                      height: '30px',
                      borderRadius: '8px',
                      background: 'transparent',
                      color: T.textTertiary,
                      transition: `all 0.2s ${T.ease}`,
                      flexShrink: 0,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,59,48,0.08)'; e.currentTarget.style.color = T.error; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.textTertiary; }}
                  >
                    <Icon name="trash" size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Repurpose panel */}
        {selected && (
          <Card className="fade-in-up" style={{ padding: '32px', animation: 'scaleIn 0.3s ease both' }}>
            <div style={{ marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 600, letterSpacing: '-0.03em', marginBottom: '4px' }}>
                Generate
              </h2>
              <p style={{ fontSize: '14px', color: T.textSecondary }}>
                "{selected.title}" → choose an output format.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '20px' }}>
              {OUTPUT_TYPES.map(ot => (
                <button
                  key={ot.id}
                  onClick={() => setOutputType(ot.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 14px',
                    borderRadius: T.radius,
                    border: `1.5px solid ${outputType === ot.id ? T.accent : T.border}`,
                    background: outputType === ot.id ? T.accentLight : T.surface,
                    transition: `all 0.2s ${T.ease}`,
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '8px',
                    background: outputType === ot.id ? ot.color : T.surfaceHover,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: `all 0.2s ${T.ease}`,
                  }}>
                    <Icon name={ot.icon} size={14} color={outputType === ot.id ? '#FFF' : T.textTertiary} />
                  </div>
                  <span style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: outputType === ot.id ? T.text : T.textSecondary,
                  }}>
                    {ot.label}
                  </span>
                </button>
              ))}
            </div>

            {message && (
              <p style={{
                fontSize: '13px',
                padding: '10px 14px',
                borderRadius: '8px',
                background: isError ? 'rgba(255,59,48,0.08)' : 'rgba(52,199,89,0.08)',
                color: isError ? T.error : T.success,
                marginBottom: '16px',
              }}>
                {message}
              </p>
            )}

            <Button variant="gradient" size="lg" loading={repurposing} icon="sparkle" onClick={handleRepurpose} style={{ width: '100%' }}>
              Generate
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   GENERATED CONTENT PAGE
   ───────────────────────────────────────────── */

function GeneratedPage({ repurposed, token, onRefresh }) {
  const [copiedId, setCopiedId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  function copy(text, id) {
    const withAttribution = text + '\n\n— Made with Repurpose AI (repurpose-ai.io)';
    navigator.clipboard.writeText(withAttribution);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  function toggleExpand(id) {
    setExpandedId(expandedId === id ? null : id);
  }

  async function deleteGenerated(id) {
    try {
      const res = await fetch(`${API_URL}/api/repurpose/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) onRefresh();
    } catch (err) {
      console.error('Delete error:', err);
    }
  }

  if (repurposed.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 20px', color: T.textTertiary }}>
        <Icon name="sparkle" size={40} color={T.textTertiary} style={{ margin: '0 auto 16px', display: 'block' }} />
        <p style={{ fontSize: '17px', fontWeight: 500, marginBottom: '4px', color: T.textSecondary }}>No generated content yet</p>
        <p style={{ fontSize: '14px' }}>Upload content and hit Generate to see results here.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
      {repurposed.map((item, i) => {
        const ot = OUTPUT_TYPES.find(o => o.id === item.output_type) || { label: item.output_type, color: T.textTertiary, icon: 'sparkle' };
        const isExpanded = expandedId === item.id;
        return (
          <Card
            key={item.id}
            hover
            className={`fade-in-up fade-in-up-${Math.min(i + 1, 5)}`}
            style={{ padding: '24px', display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '8px',
                  background: ot.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon name={ot.icon} size={14} color="#FFF" />
                </div>
                <span style={{ fontSize: '13px', fontWeight: 600, color: T.text }}>{ot.label}</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <button
                  onClick={() => copy(item.generated_text, item.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 500,
                    borderRadius: T.radiusFull,
                    background: copiedId === item.id ? 'rgba(52,199,89,0.1)' : T.surfaceHover,
                    color: copiedId === item.id ? T.success : T.textSecondary,
                    transition: `all 0.2s ${T.ease}`,
                  }}
                >
                  <Icon name={copiedId === item.id ? 'check' : 'copy'} size={12} />
                  {copiedId === item.id ? 'Copied' : 'Copy'}
                </button>
                <button
                  onClick={() => deleteGenerated(item.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    borderRadius: '8px',
                    background: 'transparent',
                    color: T.textTertiary,
                    transition: `all 0.2s ${T.ease}`,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,59,48,0.08)'; e.currentTarget.style.color = T.error; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.textTertiary; }}
                >
                  <Icon name="trash" size={13} />
                </button>
              </div>
            </div>

            <div
              onClick={() => toggleExpand(item.id)}
              style={{ cursor: 'pointer', flex: 1 }}
            >
              <p style={{
                fontSize: '14px',
                lineHeight: 1.7,
                color: T.text,
                whiteSpace: 'pre-wrap',
                ...(isExpanded ? {} : {
                  display: '-webkit-box',
                  WebkitLineClamp: 6,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }),
                transition: `all 0.3s ${T.ease}`,
              }}>
                {item.generated_text}
              </p>

              {!isExpanded && item.generated_text.length > 300 && (
                <p style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: T.accent,
                  marginTop: '8px',
                }}>
                  Show more
                </p>
              )}
              {isExpanded && (
                <p style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: T.accent,
                  marginTop: '8px',
                }}>
                  Show less
                </p>
              )}
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '16px',
              paddingTop: '12px',
              borderTop: `1px solid ${T.borderLight}`,
            }}>
              <p style={{ fontSize: '12px', color: T.textTertiary }}>
                {new Date(item.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
              <p style={{ fontSize: '11px', color: T.textTertiary }}>
                repurpose-ai.io
              </p>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────
   PRICING PAGE
   ───────────────────────────────────────────── */

function PricingPage({ currentPlan, token }) {
  const [loading, setLoading] = useState(false);

  async function handleUpgrade(plan) {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (err) {
      console.error('Upgrade error:', err);
    } finally {
      setLoading(false);
    }
  }

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '£0',
      period: 'forever',
      desc: 'For exploring what Repurpose AI can do.',
      features: ['3 repurposings per month', 'All content types', 'All output formats'],
      highlight: false,
    },
    {
      id: 'pro',
      name: 'Pro',
      price: '£9.95',
      period: '/month',
      desc: 'For creators who publish consistently.',
      features: ['Unlimited repurposings', 'Priority AI processing', 'File uploads (PDF, Word)', 'Export & download', 'Email support'],
      highlight: true,
      badge: 'Most Popular',
    },
    {
      id: 'agency',
      name: 'Agency',
      price: '£29.95',
      period: '/month',
      desc: 'For teams and agencies at scale.',
      features: ['Everything in Pro', 'Up to 5 team seats', 'White-label exports', 'API access', 'Dedicated support'],
      highlight: false,
    },
  ];

  return (
    <div style={{ maxWidth: '960px', margin: '0 auto' }}>
      <div className="fade-in-up" style={{ textAlign: 'center', marginBottom: '48px' }}>
        <h2 style={{ fontSize: '32px', fontWeight: 700, letterSpacing: '-0.04em', marginBottom: '8px' }}>
          Simple pricing
        </h2>
        <p style={{ fontSize: '17px', color: T.textSecondary }}>
          Start free. Upgrade when you're ready.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
        {plans.map((plan, i) => {
          const isCurrent = currentPlan === plan.id;
          return (
            <Card
              key={plan.id}
              className={`fade-in-up fade-in-up-${i + 1}`}
              style={{
                padding: '32px',
                position: 'relative',
                border: plan.highlight ? `2px solid ${T.accent}` : `1px solid ${T.border}`,
              }}
            >
              {plan.badge && (
                <Badge variant="accent" style={{ position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)' }}>
                  {plan.badge}
                </Badge>
              )}

              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px' }}>{plan.name}</h3>
                <p style={{ fontSize: '13px', color: T.textSecondary }}>{plan.desc}</p>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <span style={{ fontSize: '40px', fontWeight: 700, letterSpacing: '-0.04em' }}>{plan.price}</span>
                <span style={{ fontSize: '14px', color: T.textSecondary, marginLeft: '4px' }}>{plan.period}</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '28px' }}>
                {plan.features.map((f, fi) => (
                  <div key={fi} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '18px', height: '18px', borderRadius: '50%',
                      background: plan.highlight ? T.accentLight : T.surfaceHover,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <Icon name="check" size={10} color={plan.highlight ? T.accent : T.textSecondary} />
                    </div>
                    <span style={{ fontSize: '14px', color: T.textSecondary }}>{f}</span>
                  </div>
                ))}
              </div>

              {plan.id === 'free' ? (
                <Button
                  variant="secondary"
                  size="lg"
                  disabled={isCurrent}
                  style={{ width: '100%' }}
                >
                  {isCurrent ? 'Current Plan' : 'Free'}
                </Button>
              ) : (
                <Button
                  variant={plan.highlight ? 'gradient' : 'primary'}
                  size="lg"
                  loading={loading}
                  disabled={isCurrent}
                  onClick={() => handleUpgrade(plan.id)}
                  style={{ width: '100%' }}
                >
                  {isCurrent ? 'Current Plan' : 'Upgrade'}
                </Button>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   DASHBOARD
   ───────────────────────────────────────────── */

function Dashboard() {
  const { user, token, logout } = useAuth();
  const [tab, setTab] = useState('upload');
  const [content, setContent] = useState([]);
  const [repurposed, setRepurposed] = useState([]);

  const fetchData = React.useCallback(async () => {
    try {
      const [cRes, rRes] = await Promise.all([
        fetch(`${API_URL}/api/content`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/repurpose`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (cRes.ok) setContent(await cRes.json());
      if (rRes.ok) setRepurposed(await rRes.json());
    } catch (err) {
      console.error('Fetch error:', err);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchData();
  }, [token, fetchData]);

  const tabs = [
    { id: 'upload', label: 'Create', icon: 'upload' },
    { id: 'generated', label: 'Generated', icon: 'sparkle', count: repurposed.length },
    { id: 'pricing', label: 'Pricing', icon: 'credit' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: T.bg }}>
      <Nav user={user} onLogout={logout} />

      <div style={{ maxWidth: '1120px', margin: '0 auto', padding: '32px 32px 80px' }}>
        <div className="fade-in-up" style={{ marginBottom: '32px' }}>
          <TabBar tabs={tabs} active={tab} onChange={setTab} />
        </div>

        {tab === 'upload' && <UploadPage content={content} onRefresh={fetchData} token={token} />}
        {tab === 'generated' && <GeneratedPage repurposed={repurposed} token={token} onRefresh={fetchData} />}
        {tab === 'pricing' && <PricingPage currentPlan={user?.plan} token={token} />}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   APP ROOT
   ───────────────────────────────────────────── */

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: T.heroGradient,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          animation: 'pulse 1.5s ease infinite',
        }}>
          <Icon name="repurpose" size={32} color="#FFF" />
          <span style={{ fontSize: '20px', fontWeight: 600, color: '#FFF', letterSpacing: '-0.03em' }}>
            Repurpose AI
          </span>
        </div>
      </div>
    );
  }

  return user ? <Dashboard /> : <AuthScreen />;
}

function Root() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}

export default Root;
